/**
 * TranscriptionItem — Editable segment row for the transcriptions plugin.
 *
 * Renders a single transcript segment with speaker label, channel dominance
 * badge, and supports inline editing (Enter to save, Escape to cancel).
 * Adapted from live-tranz's EditableTranscriptItem.tsx.
 */

import type { TranscriptSegment } from './types'
import { createSignal, Show } from 'solid-js'
import { SpeakerLabel } from './SpeakerLabel'

interface Props {
  segment: TranscriptSegment
  onEditSave?: (segmentId: string, newText: string) => void
  showSpeakerLabel?: boolean
  isGrouped?: boolean
  hideRawApplogs?: boolean
}

/**
 * Channel dominance badge — compact pill showing L/R/balanced with percentage.
 */
function ChannelBadge(props: { hint: string | null }) {
  if (!props.hint) return null

  const parts = props.hint.split(':')
  if (parts.length !== 2) return null

  const [, pctStr] = parts
  const pct = Math.round(Number.parseFloat(pctStr) * 100)
  if (Number.isNaN(pct)) return null

  let label: string
  let color: string

  if (props.hint.startsWith('left')) {
    label = `L ${pct}%`
    color = '#3b82f6' // blue
  } else if (props.hint.startsWith('right')) {
    label = `R ${pct}%`
    color = '#a855f7' // purple
  } else {
    label = `= ${pct}%`
    color = '#6b7280' // gray
  }

  return (
    <span
      class="text-10px font-medium px-1 py-0.5 rounded flex-shrink-0"
      style={{
        'background-color': color + '22',
        color,
        'border': `1px solid ${color}44`,
      }}
      title={`Channel dominance: ${props.hint}`}
    >
      {label}
    </span>
  )
}

export function TranscriptionItem(props: Props) {
  const [editing, setEditing] = createSignal(false)
  const [editText, setEditText] = createSignal('')
  const [expanded, setExpanded] = createSignal(false)
  let textareaRef: HTMLTextAreaElement | undefined

  const startEditing = () => {
    setEditText(props.segment.text)
    setEditing(true)
  }

  const cancelEditing = () => {
    setEditing(false)
    setEditText('')
  }

  const saveEdit = () => {
    const newText = editText().trim()
    if (newText && newText !== props.segment.text) {
      props.onEditSave?.(props.segment.segmentId, newText)
    }
    setEditing(false)
    setEditText('')
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      saveEdit()
    } else if (e.key === 'Escape') {
      cancelEditing()
    }
  }

  const isDeleted = () => props.segment.isDeleted

  return (
    <div
      class={`group flex flex-col gap-0 py-3 px-4 rounded transition-colors ${
        editing()
          ? 'bg-primary/10 ring-1 ring-primary'
          : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-text'
      } ${
        props.segment.isEdited ? 'border-l-2 border-yellow-500' : ''
      } ${
        props.isGrouped ? 'border-l-2 border-transparent ml-4' : ''
      } ${
        isDeleted() ? 'opacity-50' : ''
      }`}
    >
      {/* Main row */}
      <div class="flex gap-2 items-start">
        {/* Channel dominance badge */}
        <ChannelBadge hint={props.segment.channelDominanceHint} />

        {/* Edit indicator */}
        <Show when={props.segment.isEdited}>
          <span class="text-yellow-500 text-xs flex-shrink-0" title="Edited">✎</span>
        </Show>

        {/* Speaker label — shown only when not grouped with previous */}
        <Show when={props.showSpeakerLabel && !props.isGrouped}>
          <div class="flex-shrink-0">
            <Show
              when={props.segment.speakerLabel}
              fallback={
                /* No speaker assigned */
                <span class="text-10px text-neutral-400 italic px-1">unknown</span>
              }
            >
              <SpeakerLabel
                label={props.segment.speakerLabel!}
                confidence={props.segment.speakerConfidence ?? undefined}
                source={props.segment.speakerSource ?? undefined}
              />
            </Show>
          </div>
        </Show>

        {/* Transcript text or edit textarea */}
        <Show
          when={editing()}
          fallback={
            <div class="flex-1 min-w-0 flex items-start gap-2">
              <span
                class={`flex-1 text-base leading-relaxed text-neutral-900 dark:text-neutral-100 ${
                  isDeleted() ? 'line-through' : ''
                }`}
                onClick={() => !isDeleted() && startEditing()}
              >
                {props.segment.text}
              </span>

              {/* Expand/collapse toggle */}
              <button
                type="button"
                class="text-10px px-1 py-0.5 rounded border border-neutral-300 dark:border-neutral-600 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 flex-shrink-0 self-start"
                title={expanded() ? 'Collapse details' : 'Expand details'}
                onClick={(e) => { e.stopPropagation(); setExpanded(!expanded()) }}
              >
                {expanded() ? '▾' : '▸'}
              </button>

              {/* Edit hint on hover */}
              <Show when={!isDeleted()}>
                <span class="text-xs text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 self-center">
                  click to edit
                </span>
              </Show>
            </div>
          }
        >
          <textarea
            ref={(el) => {
              textareaRef = el
              window.setTimeout(() => {
                el.focus()
                el.select()
              }, 0)
            }}
            value={editText()}
            onInput={(e) => setEditText(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveEdit}
            class="flex-1 text-base leading-relaxed bg-transparent border-none outline-none resize-none p-0 min-h-[1.5em] text-neutral-900 dark:text-neutral-100"
            rows={Math.max(1, (editText() || '').split('\n').length)}
          />
        </Show>

        {/* Editing hints */}
        <Show when={editing()}>
          <span class="text-10px text-neutral-400 flex-shrink-0 self-center whitespace-nowrap">
            Enter save · Esc cancel
          </span>
        </Show>
      </div>

      {/* Expanded details panel */}
      <Show when={expanded()}>
        <div class="mt-2 pt-2 border-t border-neutral-200 dark:border-neutral-700 text-xs text-neutral-500">
          <div class="grid grid-cols-2 gap-x-4 gap-y-1">
            <span>Segment ID:</span>
            <span class="font-mono text-10px">{props.segment.segmentId}</span>
            <span>Start:</span>
            <span>{props.segment.startMs}ms</span>
            <span>End:</span>
            <span>{props.segment.endMs}ms</span>
            <span>Duration:</span>
            <span>{props.segment.endMs - props.segment.startMs}ms</span>
            <Show when={props.segment.speakerId}>
              <span>Speaker ID:</span>
              <span class="font-mono text-10px">{props.segment.speakerId}</span>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  )
}
